/**
 * Leadership Burden Diagnostic Page (v2.0)
 *
 * Anxiety-based diagnostic using 10 psychological inflexibility measures
 * to assess leadership burden and calculate value recovery potential.
 *
 * v2.0 Features:
 * - Persona selection (Mark Ellison or Sarah Lawson)
 * - "How often?" question framing (inflexibility)
 * - Calculated LPFF from anxiety scores
 * - Evidence-based constants (McKinsey 2021, Skews 2018)
 * - Combined Recovery Factor (CRF) with ACT multiplier
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PersonaSelector from '../components/PersonaSelector';
import Footer from '../components/Footer';
import {
  calculateLeadershipBurdenV2,
  PERSONA_BASELINES
} from '../utils/algorithms';

function LeadershipBurdenDiagnostic() {
  const [searchParams] = useSearchParams();
  const [validationState, setValidationState] = useState('loading');
  const [guidCode, setGuidCode] = useState(null);
  const [currentStep, setCurrentStep] = useState('persona'); // 'persona' | 'value' | 'anxieties' | 'results'

  const [leaderInfo, setLeaderInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: ''
  });

  const [selectedPersona, setSelectedPersona] = useState('mark'); // Default to Mark
  const [valueAtStake, setValueAtStake] = useState('');

  const [anxietyScores, setAnxietyScores] = useState({
    anx1: 5, anx2: 5, anx3: 5, anx4: 5, anx5: 5,
    anx6: 5, anx7: 5, anx8: 5, anx9: 5, anx10: 5
  });

  const [results, setResults] = useState(null);

  // 10 anxieties with "How often?" framing (measuring inflexibility)
  const anxieties = [
    { id: 1, name: 'Imposter Complex', question: 'How often do you feel like a fraud or fear being exposed as inadequate in your leadership role?' },
    { id: 2, name: 'Fragile Authority', question: 'How often do you question whether people truly trust or respect your leadership decisions?' },
    { id: 3, name: 'Over-Dependence', question: 'How often do you fear the organization will collapse without your constant involvement?' },
    { id: 4, name: 'Moral Injury', question: 'How often do you feel forced to compromise your values or integrity in transformation decisions?' },
    { id: 5, name: 'Invisible Labour', question: 'How often do you feel your emotional work and behind-the-scenes efforts go unrecognized?' },
    { id: 6, name: 'Failing the Future', question: 'How often do you worry that your transformation efforts will be wasted or leave no lasting legacy?' },
    { id: 7, name: 'Team Disengagement', question: 'How often does team apathy or lack of ownership make you feel helpless as a leader?' },
    { id: 8, name: 'Personal Depletion', question: 'How often do you feel burned out or lacking the energy to sustain your leadership?' },
    { id: 9, name: 'Identity Drift', question: 'How often do you feel like you\'re losing your sense of self in your leadership role?' },
    { id: 10, name: 'Loss of Meaning', question: 'How often do you question whether your transformation work has any real purpose or meaning?' }
  ];

  // Validate GUID on component mount
  useEffect(() => {
    const code = searchParams.get('code');

    if (!code) {
      setValidationState('invalid');
      return;
    }

    const validateCode = async () => {
      try {
        const response = await fetch(`/api/validate-code?code=${code}`);
        const data = await response.json();

        if (data.success && data.valid) {
          setGuidCode(code);

          if (data.completed) {
            setValidationState('completed');
          } else {
            setValidationState('valid');
            setLeaderInfo({
              firstName: data.leader?.firstName || '',
              lastName: data.leader?.lastName || '',
              email: data.leader?.email || '',
              role: ''
            });
          }
        } else {
          setValidationState('invalid');
        }
      } catch (error) {
        console.error('GUID validation error:', error);
        setValidationState('invalid');
      }
    };

    validateCode();
  }, [searchParams]);

  const handleSliderChange = (anxietyId, value) => {
    setAnxietyScores(prev => ({
      ...prev,
      [`anx${anxietyId}`]: parseInt(value)
    }));
  };

  const handlePersonaContinue = () => {
    setCurrentStep('value');
    window.scrollTo(0, 0);
  };

  const handleValueContinue = () => {
    const value = parseFloat(valueAtStake);
    if (value > 0 && value <= 1000) {
      setCurrentStep('anxieties');
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    const value = parseFloat(valueAtStake);

    // Calculate results using v2.0 algorithm
    const calculations = calculateLeadershipBurdenV2(value, anxietyScores, selectedPersona);

    // Get top 3 anxieties for recommendations
    const scorePairs = anxieties.map(anxiety => ({
      ...anxiety,
      score: anxietyScores[`anx${anxiety.id}`]
    }));
    const topAnxieties = scorePairs.sort((a, b) => b.score - a.score).slice(0, 3);

    setResults({ calculations, topAnxieties });

    // Save results to database
    if (guidCode) {
      try {
        await fetch('/api/save-leadership-burden-results', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            code: guidCode,
            personaId: selectedPersona,
            valueAtStake: value,
            anxietyScores,
            calculations,
            topAnxieties
          })
        });
      } catch (error) {
        console.error('Failed to save results:', error);
        // Continue showing results even if save fails
      }
    }

    setCurrentStep('results');
    window.scrollTo(0, 0);
  };

  const handleStartOver = () => {
    setCurrentStep('persona');
    setSelectedPersona('mark');
    setValueAtStake('');
    setAnxietyScores({
      anx1: 5, anx2: 5, anx3: 5, anx4: 5, anx5: 5,
      anx6: 5, anx7: 5, anx8: 5, anx9: 5, anx10: 5
    });
    setResults(null);
    window.scrollTo(0, 0);
  };

  // Loading state
  if (validationState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-daring-navy mx-auto mb-4"></div>
          <p className="text-daring-grey">Validating your diagnostic link...</p>
        </div>
      </div>
    );
  }

  // Invalid state
  if (validationState === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-daring-navy mb-4">
            Invalid or Expired Link
          </h1>
          <p className="text-daring-grey mb-6">
            This diagnostic link is invalid, has expired, or has already been used.
            Please request a new diagnostic link.
          </p>
          <a
            href="/request-diagnostic"
            className="inline-block bg-daring-teal hover:bg-daring-navy text-white font-bold px-6 py-3 rounded transition-all"
          >
            Request New Diagnostic
          </a>
        </div>
      </div>
    );
  }

  // Completed state
  if (validationState === 'completed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-green-500 text-5xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-daring-navy mb-4">
            Diagnostic Already Completed
          </h1>
          <p className="text-daring-grey mb-6">
            You've already completed this diagnostic. Your results have been saved
            and sent to your email.
          </p>
          <a
            href="/request-diagnostic"
            className="inline-block bg-daring-teal hover:bg-daring-navy text-white font-bold px-6 py-3 rounded transition-all"
          >
            Request New Diagnostic
          </a>
        </div>
      </div>
    );
  }

  // Step 1: Persona Selection
  if (currentStep === 'persona') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h1 className="text-3xl font-bold text-daring-navy mb-4">
              Leadership Burden Diagnostic
            </h1>
            <p className="text-daring-grey mb-4">
              Welcome, <strong>{leaderInfo.firstName} {leaderInfo.lastName}</strong>
            </p>
            <p className="text-daring-grey">
              This diagnostic assesses leadership anxieties to calculate your transformation value at risk.
            </p>
          </div>

          {/* Persona Selector */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <PersonaSelector
              selectedPersona={selectedPersona}
              onChange={setSelectedPersona}
              diagnosticType="leadership_burden"
            />
          </div>

          {/* Continue Button */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <button
              onClick={handlePersonaContinue}
              disabled={!selectedPersona}
              className="w-full bg-daring-teal hover:bg-daring-navy text-white font-bold py-4 px-6 rounded text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Value Assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Value at Stake
  if (currentStep === 'value') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h1 className="text-3xl font-bold text-daring-navy mb-4">
              Transformation Value at Stake
            </h1>
            <p className="text-daring-grey">
              Estimate the annual value (in millions) that your transformation aims to capture or protect.
            </p>
          </div>

          {/* Value Input */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <label className="block text-lg font-bold text-daring-navy mb-4">
              Transformation Value (millions $)
            </label>
            <input
              type="number"
              min="0.1"
              max="1000"
              step="0.1"
              value={valueAtStake}
              onChange={(e) => setValueAtStake(e.target.value)}
              placeholder="e.g., 10.5"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-2xl font-bold text-daring-teal focus:border-daring-teal focus:outline-none"
            />
            <p className="text-sm text-daring-grey mt-2">
              This represents the total annual value your transformation is designed to deliver.
            </p>
          </div>

          {/* Navigation */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentStep('persona')}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-daring-navy font-bold py-3 px-6 rounded transition-all"
              >
                Back
              </button>
              <button
                onClick={handleValueContinue}
                disabled={!valueAtStake || parseFloat(valueAtStake) <= 0}
                className="flex-1 bg-daring-teal hover:bg-daring-navy text-white font-bold py-3 px-6 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Anxieties
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Anxiety Assessment
  if (currentStep === 'anxieties') {
    const persona = PERSONA_BASELINES[selectedPersona];

    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h1 className="text-3xl font-bold text-daring-navy mb-4">
              Leadership Anxiety Assessment
            </h1>
            <p className="text-daring-grey mb-4">
              For each anxiety, rate how often you experience it from 0 (constantly) to 10 (never).
            </p>
            <div className="bg-blue-50 border-l-4 border-daring-teal p-4 rounded">
              <p className="text-sm text-daring-navy">
                <strong>Your Profile:</strong> {persona.role} • {persona.strain}<br />
                <strong>Organizational Capability Baseline:</strong> {(persona.dcf * 100).toFixed(0)}% (held constant)<br />
                <strong>How to interpret:</strong> Higher scores (experiencing anxiety rarely) indicate better psychological flexibility
              </p>
            </div>
          </div>

          {/* Anxiety Sliders */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-daring-navy mb-6">Rate Each Anxiety</h2>

            <div className="space-y-8">
              {anxieties.map((anxiety) => (
                <div key={anxiety.id} className="border-b border-gray-200 pb-6 last:border-0">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-daring-navy text-lg">
                        {anxiety.id}. {anxiety.name}
                      </h3>
                      <p className="text-sm text-daring-grey mt-1">{anxiety.question}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <span className="text-3xl font-bold text-daring-teal">
                        {anxietyScores[`anx${anxiety.id}`]}
                      </span>
                      <span className="text-sm text-daring-grey block">/10</span>
                    </div>
                  </div>

                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={anxietyScores[`anx${anxiety.id}`]}
                    onChange={(e) => handleSliderChange(anxiety.id, e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-daring-teal"
                  />

                  <div className="flex justify-between text-xs text-daring-grey mt-1">
                    <span>0 - Constantly</span>
                    <span>5 - Sometimes</span>
                    <span>10 - Never</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentStep('value')}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-daring-navy font-bold py-3 px-6 rounded transition-all"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 bg-daring-teal hover:bg-daring-navy text-white font-bold py-4 px-6 rounded text-lg transition-all"
              >
                Generate Results
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 4: Results
  const persona = PERSONA_BASELINES[selectedPersona];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Overall Results */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h1 className="text-3xl font-bold text-daring-navy mb-6">
            Your Leadership Burden Assessment
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="text-4xl font-bold text-red-600 mb-2">
                ${results.calculations.totalLoss.toFixed(2)}M
              </div>
              <div className="text-sm text-daring-grey">Total Value at Risk</div>
              <div className="text-xs text-daring-grey mt-1">
                (McKinsey average: 43% of ${parseFloat(valueAtStake).toFixed(1)}M)
              </div>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="text-4xl font-bold text-green-600 mb-2">
                ${results.calculations.recoverableValue.toFixed(2)}M
              </div>
              <div className="text-sm text-daring-grey">Recoverable Value</div>
              <div className="text-xs text-daring-grey mt-1">
                (Based on your anxieties & {persona.role} profile)
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-3xl font-bold text-daring-navy mb-2">
                {(results.calculations.dcf * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-daring-grey">Organizational Capability</div>
              <div className="text-xs text-daring-grey mt-1">(DCF - {persona.role})</div>
            </div>

            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-3xl font-bold text-daring-navy mb-2">
                {(results.calculations.lpff * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-daring-grey">Leadership Flexibility</div>
              <div className="text-xs text-daring-grey mt-1">(LPFF - Calculated)</div>
            </div>

            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-3xl font-bold text-daring-navy mb-2">
                {(results.calculations.crf * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-daring-grey">Combined Recovery</div>
              <div className="text-xs text-daring-grey mt-1">(CRF with ACT boost)</div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-daring-teal p-6 rounded mb-6">
            <p className="text-daring-navy mb-2">
              <strong>Final Capture Rate:</strong> {(results.calculations.finalCaptureRate * 100).toFixed(1)}%
            </p>
            <p className="text-sm text-daring-grey">
              {results.calculations.isValid
                ? `✅ Within McKinsey's successful transformation ceiling (67%)`
                : `⚠️ Exceeds McKinsey's successful transformation ceiling (67%) - results may be optimistic`
              }
            </p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
            <p className="text-sm text-daring-navy">
              <strong>Value Breakdown (McKinsey 2021 Research):</strong><br />
              System Loss: ${results.calculations.systemLoss.toFixed(2)}M (68.5% of total risk)<br />
              Leadership Loss: ${results.calculations.leadershipLoss.toFixed(2)}M (31.5% of total risk)
            </p>
          </div>
        </div>

        {/* Top 3 Anxieties */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-daring-navy mb-6">
            Top 3 Anxieties Requiring Attention
          </h2>

          <div className="space-y-6">
            {results.topAnxieties.map((anxiety, index) => (
              <div key={anxiety.id} className="border-l-4 border-daring-teal pl-6 py-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-bold text-daring-navy">
                    {index + 1}. {anxiety.name}
                  </h3>
                  <span className="text-2xl font-bold text-daring-teal ml-4">
                    {anxiety.score}/10
                  </span>
                </div>

                <p className="text-daring-grey mb-3">
                  {anxiety.question}
                </p>

                <div className="bg-green-50 p-4 rounded">
                  <p className="text-sm text-daring-navy">
                    <strong>ACT Intervention Focus:</strong> Work with a coach on psychological flexibility techniques to address this pattern (8% proven effectiveness per Skews et al. 2018)
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Algorithm Details */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-daring-navy mb-4">
            How We Calculate Your Results
          </h2>

          <div className="space-y-4 text-sm text-daring-grey">
            <div className="bg-gray-50 p-4 rounded">
              <p className="font-bold text-daring-navy mb-2">Evidence-Based Constants:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Average transformation value loss: 43% (McKinsey 2021)</li>
                <li>Successful transformation ceiling: 67% capture rate (McKinsey 2021)</li>
                <li>Maximum recovery rate: 23.3% (McKinsey 2021)</li>
                <li>ACT coaching effectiveness: 8% improvement (Skews et al. 2018)</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded">
              <p className="font-bold text-daring-navy mb-2">Your Calculations:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Total Value Loss = ${parseFloat(valueAtStake).toFixed(2)}M × 43% = ${results.calculations.totalLoss.toFixed(2)}M</li>
                <li>Organizational Capability (DCF) = {(persona.dcf * 100).toFixed(0)}% ({persona.role} baseline)</li>
                <li>Leadership Flexibility (LPFF) = (100 - Average anxiety score) / 10 = {(results.calculations.lpff * 100).toFixed(1)}%</li>
                <li>Combined Recovery Factor (CRF) = (DCF × 68.5%) + (LPFF × 31.5% × 1.08) = {(results.calculations.crf * 100).toFixed(1)}%</li>
                <li>Recoverable Value = ${results.calculations.totalLoss.toFixed(2)}M × {(results.calculations.crf * 100).toFixed(1)}% × 23.3% = ${results.calculations.recoverableValue.toFixed(2)}M</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => window.print()}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-daring-navy font-bold py-3 px-6 rounded transition-all"
            >
              Print Results
            </button>

            <button
              onClick={handleStartOver}
              className="flex-1 bg-daring-teal hover:bg-daring-navy text-white font-bold py-3 px-6 rounded transition-all"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default LeadershipBurdenDiagnostic;
